import React, { FC, useEffect, useRef, useState } from 'react';
import './Tooltip.scss';
import PopupPositions from '../../ts_classes/PopupPositions';
import LocatePopup from '../../ts_classes/LocatePopup';
import { ElementCoordinate } from '../../enums/enums';

interface TooltipProps {
   content: any;
   children: React.ReactNode;
}

const Tooltip: FC<TooltipProps> = ({ content, children }) => {
   const [isVisible, setIsVisible] = useState(false);
   const [position, setPosition] = useState({ top: "0", left: "0" });
   const tooltipRef = useRef<HTMLDivElement>(null);
   const targetRef = useRef<HTMLDivElement>(null);

   /* #region Functions */
   // hover is a real, sustained signal only from an actual mouse - touch/pen
   // send synthetic enter+leave events back-to-back right after a tap
   const handlePointerEnter = (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      setIsVisible(true);
   };

   const handlePointerLeave = (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      setIsVisible(false);
   };

   const handlePointerUp = (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse') return; // mouse users rely on hover, not tap
      setIsVisible(v => !v);
   };
   /* #endregion Functions */

   /* #region useEffects */

   // on touch devices there's no hover/leave, so tap-to-toggle and tap-away-to-close instead
   useEffect(() => {
      if (!isVisible) return;
      const handleOutsideInteraction = (e: MouseEvent) => {
         if (targetRef.current && !targetRef.current.contains(e.target as Node)) {
            setIsVisible(false);
         }
      };
      // defer registration by a tick - otherwise the trailing compatibility "click"
      // that a touch tap fires right after opening the tooltip gets caught by this
      // same listener and closes it immediately
      const timeoutId = window.setTimeout(() => {
         document.addEventListener('click', handleOutsideInteraction);
      }, 0);
      return () => {
         window.clearTimeout(timeoutId);
         document.removeEventListener('click', handleOutsideInteraction);
      };
   }, [isVisible]);

   useEffect(() => {
      if (isVisible && targetRef.current && tooltipRef.current) {
         const targetRect = targetRef.current.getBoundingClientRect();
         const tooltipRect = tooltipRef.current.getBoundingClientRect();
         const targetRefCSSStyleDeclaration = getComputedStyle(targetRef.current);
         const positions = new PopupPositions(targetRect, tooltipRect, targetRefCSSStyleDeclaration).returnAvailablePositions();
         let popupUpperLeftLocation = {xPx:"0", yPx:"0"};
      
         //order is important (uc is the best, followed by lc, etc.)
         switch (true) {
            case positions.UPPER_CENTER:
               popupUpperLeftLocation = new LocatePopup(targetRect, tooltipRef.current, tooltipRect, ElementCoordinate.UpperCenter).getLocation();
               console.log("UPPER_CENTER");
               break;
            case positions.LOWER_CENTER:
               popupUpperLeftLocation = new LocatePopup(targetRect, tooltipRef.current, tooltipRect, ElementCoordinate.LowerCenter).getLocation();
               console.log("LOWER_CENTER");
               break;
            case positions.CENTER_RIGHT:
               popupUpperLeftLocation = new LocatePopup(targetRect, tooltipRef.current, tooltipRect, ElementCoordinate.CenterRight).getLocation();
               console.log("CENTER_RIGHT");
               break; 
             case positions.CENTER_LEFT:
               popupUpperLeftLocation = new LocatePopup(targetRect, tooltipRef.current, tooltipRect, ElementCoordinate.CenterLeft).getLocation();
               console.log("CENTER_LEFT");
              break;
            case positions.UPPER_RIGHT:
               popupUpperLeftLocation = new LocatePopup(targetRect, tooltipRef.current, tooltipRect, ElementCoordinate.UpperRight).getLocation();
               console.log("UPPER_RIGHT");
               break;
            case positions.LOWER_RIGHT:
               popupUpperLeftLocation = new LocatePopup(targetRect, tooltipRef.current, tooltipRect, ElementCoordinate.LowerRight).getLocation();
               console.log("LOWER_RIGHT");
               break; 
            case positions.UPPER_LEFT:
               popupUpperLeftLocation = new LocatePopup(targetRect, tooltipRef.current, tooltipRect, ElementCoordinate.UpperLeft).getLocation();
               console.log("UPPER_LEFT");
               break;
            case positions.LOWER_LEFT:
               popupUpperLeftLocation = new LocatePopup(targetRect, tooltipRef.current, tooltipRect, ElementCoordinate.LowerLeft).getLocation();
               console.log("LOWER_LEFT");
               break;
            case positions.MOBILE_ABOVE:
               popupUpperLeftLocation = new LocatePopup(targetRect, tooltipRef.current, tooltipRect, ElementCoordinate.MobileAbove).getLocation();
               console.log("MOBILE_ABOVE");
               break;
            case positions.MOBILE_BELOW:
               popupUpperLeftLocation = new LocatePopup(targetRect, tooltipRef.current, tooltipRect, ElementCoordinate.MobileBelow).getLocation();
               console.log("MOBILE_BELOW");
               break;
            default:
               console.error("Could not find proper pop-up location! tooltip.js > locateTooltip");
         }
         console.log("Positions", positions);
         const left = popupUpperLeftLocation.xPx;
         const top = popupUpperLeftLocation.yPx;
         console.log("left", left, "top", top);

         setPosition({ left, top });
      }
   }, [isVisible]);

   /* #endregion useEffects */

   return (
      <div
         className='tooltip-container'
         ref={targetRef}
         onPointerEnter={handlePointerEnter}
         onPointerLeave={handlePointerLeave}
         onPointerUp={handlePointerUp}
      >
         {children}

         {isVisible && (
            <div
               className="tooltip"
               ref={tooltipRef}
               style={{ top: position.top, left: position.left }}
            >
               {content}
            </div>
         )}
      </div>
   );

};

export default Tooltip;
